import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { applyFieldErrors, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { FormSection, MoneyInput } from '@nucleus-commerce/admin-kit/forms';
import type { DraftOrder } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';
import { Label } from '@nucleus-commerce/ui/components/label';
import { RadioGroup, RadioGroupItem } from '@nucleus-commerce/ui/components/radio-group';

import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import { fromDraftShipping, toShippingBody } from '../api/mappers';
import { useUpdateDraftOrder } from '../hooks/use-draft-order-mutations';
import { isStateConflict } from '../schemas/draft-order-rules';
import {
  shippingSchema,
  type ShippingFormInput,
  type ShippingFormOutput,
} from '../schemas/shipping-schema';

const KNOWN_FIELDS: ReadonlySet<string> = new Set(['rateId', 'title', 'price']);

/** `shipping.price.currency` belongs to the price field, `shipping.rateId` to the rate list. */
const mapField = (field: string) => {
  const rest = field.replace(/^shipping\./, '');
  return rest.startsWith('price') ? 'price' : rest;
};

const RATE = 'rate:';

export interface DraftShippingCardProps {
  storeId: string;
  draft: DraftOrder;
  editable: boolean;
}

/**
 * One choice: no charge, one of the rates the shipping address qualifies for (the backend lists
 * them on the draft), or a custom title and price.
 */
export function DraftShippingCard({ storeId, draft, editable }: DraftShippingCardProps) {
  const { t } = useTranslation(['draftOrders', 'common']);
  const format = useFormat();
  const message = useErrorMessage();
  const update = useUpdateDraftOrder(storeId, 'draftOrders:shipping.saved');
  const form = useForm<ShippingFormInput, unknown, ShippingFormOutput>({
    resolver: useI18nResolver(shippingSchema),
    defaultValues: fromDraftShipping(draft),
  });
  const mode = form.watch('mode');
  const rateId = form.watch('rateId');
  const choice = mode === 'rate' ? `${RATE}${rateId}` : mode;

  const submit = form.handleSubmit(async (values) => {
    form.clearErrors('root.server');
    try {
      await update.mutateAsync({ id: draft.id, body: toShippingBody(values) });
      form.reset(values);
    } catch (error) {
      if (applyFieldErrors(error, form.setError, { knownFields: KNOWN_FIELDS, mapField })) return;
      form.setError('root.server', {
        type: 'server',
        message: isStateConflict(error) ? t('draftOrders:errors.locked') : message(error),
      });
    }
  });

  const current = draft.shipping ? (
    <p className="text-sm">
      {t('draftOrders:shipping.current', {
        title: draft.shipping.title,
        price: format.money(draft.shippingTotal),
      })}
    </p>
  ) : (
    <p className="text-muted-foreground text-sm">{t('draftOrders:shipping.none')}</p>
  );

  if (!editable) {
    return <FormSection title={t('draftOrders:shipping.title')}>{current}</FormSection>;
  }

  return (
    <FormSection title={t('draftOrders:shipping.title')}>
      <Form {...form}>
        <form
          onSubmit={submit}
          noValidate
          className="flex flex-col gap-4"
          aria-label={t('draftOrders:shipping.title')}
        >
          {form.formState.errors.root?.server?.message ? (
            <Alert variant="destructive" role="alert">
              <AlertDescription>{form.formState.errors.root.server.message}</AlertDescription>
            </Alert>
          ) : null}
          {!draft.shippingAddress ? (
            <p className="text-muted-foreground text-sm">{t('draftOrders:shipping.noAddress')}</p>
          ) : draft.availableRates.length === 0 ? (
            <p className="text-muted-foreground text-sm">{t('draftOrders:shipping.noRates')}</p>
          ) : null}
          <FormField
            control={form.control}
            name="rateId"
            render={() => (
              <FormItem>
                <RadioGroup
                  value={choice}
                  aria-label={t('draftOrders:shipping.title')}
                  onValueChange={(value) => {
                    form.clearErrors();
                    if (value.startsWith(RATE)) {
                      form.setValue('mode', 'rate', { shouldDirty: true });
                      form.setValue('rateId', value.slice(RATE.length), { shouldDirty: true });
                    } else {
                      form.setValue('mode', value as 'none' | 'custom', { shouldDirty: true });
                    }
                  }}
                >
                  <div className="flex items-center gap-2">
                    <RadioGroupItem id="draft-shipping-none" value="none" />
                    <Label htmlFor="draft-shipping-none" className="font-normal">
                      {t('draftOrders:shipping.none')}
                    </Label>
                  </div>
                  {draft.availableRates.map((rate) => (
                    <div key={rate.id} className="flex items-center gap-2">
                      <RadioGroupItem
                        id={`draft-shipping-${rate.id}`}
                        value={`${RATE}${rate.id}`}
                      />
                      <Label htmlFor={`draft-shipping-${rate.id}`} className="font-normal">
                        {t('draftOrders:shipping.rateName', {
                          name: rate.name,
                          price: format.money(rate.price),
                        })}
                      </Label>
                    </div>
                  ))}
                  <div className="flex items-center gap-2">
                    <RadioGroupItem id="draft-shipping-custom" value="custom" />
                    <Label htmlFor="draft-shipping-custom" className="font-normal">
                      {t('draftOrders:shipping.custom')}
                    </Label>
                  </div>
                </RadioGroup>
                <FormMessage />
              </FormItem>
            )}
          />
          {mode === 'custom' ? (
            <div className="grid gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('draftOrders:shipping.customTitle')}</FormLabel>
                    <FormControl>
                      <Input autoComplete="off" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="price"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <FormLabel>{t('draftOrders:shipping.customPrice')}</FormLabel>
                    <FormControl>
                      <MoneyInput
                        currency={draft.currency}
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        aria-invalid={fieldState.invalid}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          ) : null}
          {current}
          <div>
            <Button type="submit" disabled={update.isPending || !form.formState.isDirty}>
              {update.isPending ? t('common:actions.saving') : t('draftOrders:shipping.save')}
            </Button>
          </div>
        </form>
      </Form>
    </FormSection>
  );
}
